import {
  IconButton as MuiIconButton,
  type IconButtonProps as MuiIconButtonProps,
  styled,
  type Theme,
} from "@material-ui/core";
import type {
  Palette,
  PaletteColor,
} from "@material-ui/core/styles/createPalette";

/**
 * Palette keys whose value is a full `PaletteColor` (have `main`/`light`/
 * `dark`). Structural keys like `grey`, `text` and `background` are excluded
 * because the styles read `.main` etc. off the colour.
 */
type PaletteColorName = {
  [K in keyof Palette]: Palette[K] extends PaletteColor ? K : never;
}[keyof Palette];

export interface IconButtonProps extends Omit<MuiIconButtonProps, "color"> {
  color?: PaletteColorName;
}

const StyledIconButton = styled(MuiIconButton)<
  Theme,
  { "data-color"?: PaletteColorName }
>(({ theme, ...props }) => {
  const paletteColor = theme.palette[props["data-color"] ?? "primary"];
  return {
    color: paletteColor.main,
    "&:hover": { color: paletteColor.light },
    "&:active": { color: paletteColor.dark },
    padding: theme.spacing(),
  };
});

/**
 * The standard icon-only button — a clickable icon with no background. `color`
 * accepts any palette colour and tints the icon (`main`), with `light`/`dark`
 * on hover/active. This is a distinct primitive from the standard `Button`; use
 * it for toolbar/table actions, not for labelled buttons.
 */
export const IconButton: FC<IconButtonProps> = ({
  color = "primary",
  ...props
}) => (
  /*
   * We don't pass the colour to MuiIconButton directly (it only accepts its own
   * limited union and logs errors). It's forwarded as a data attribute and read
   * back in the styles. This can be dropped once we're on MUI v5.
   */
  <StyledIconButton data-color={color} {...props} />
);
